import React from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import {
  screenSmMin,
  screenLgMin,
  IconImage,
} from '@spotify-internal/encore-web';

type ImageResolutionsType = {
  '1x': string;
  '2x'?: string;
};

export type BaseImageType = {
  useIntrinsicSizing?: boolean;
  sources: {
    small: ImageResolutionsType;
    medium?: ImageResolutionsType;
    large?: ImageResolutionsType;
  };
  label?: string;
};

export type ImagePropTypes = BaseImageType & {
  className?: string;
  children?: React.ReactNode;
};

const StyledIconImage = styled(IconImage)`
  color: #bbb;
`;

const hiresMediaQuery = `
only screen and ( min-device-pixel-ratio: 1.25),
only screen and ( min-resolution: 200dpi),
only screen and ( min-resolution: 1.25dppx)`;

/*
 * This function sets the background image as small by default,
 * along with its high resolution equivalent.
 * If medium and large images are provided, these will override
 * smaller size ones.
 * An optional "saveData" parameter is used to disable high resolution
 * images where desired.
 */
const generateBackgroundsFromProps = function generateBackgrounds(
  sources: BaseImageType['sources'],
) {
  const backgrounds: string[] = [];

  backgrounds.push(
    `background-image: url("${encodeURI(sources.small['1x'])}");`,
  );
  if (sources.small['2x']) {
    backgrounds.push(`
      html:not([data-prefers-reduced-data]) & {
        @media ${hiresMediaQuery} { background-image: url("${encodeURI(
      sources.small['2x'],
    )}"); }
      }
  `);
  }

  if (sources.medium) {
    backgrounds.push(`@media screen and (min-width: ${screenSmMin}) {`);
    backgrounds.push(
      `background-image: url("${encodeURI(sources.medium['1x'])}");`,
    );
    if (sources.medium['2x']) {
      backgrounds.push(`
        html:not([data-prefers-reduced-data]) & {
          @media ${hiresMediaQuery} { background-image: url("${encodeURI(
        sources.medium['2x'],
      )}"); }
        }
    `);
    }
    backgrounds.push(`}`);
  }

  if (sources.large) {
    backgrounds.push(`@media screen and (min-width: ${screenLgMin}) {`);
    backgrounds.push(
      `background-image: url("${encodeURI(sources.large['1x'])}");`,
    );
    if (sources.large['2x']) {
      backgrounds.push(`
        html:not([data-prefers-reduced-data]) & {
          @media ${hiresMediaQuery} { background-image: url("${encodeURI(
        sources.large['2x'],
      )}"); }
        }
    `);
    }
    backgrounds.push(`}`);
  }

  return backgrounds.join('\n');
};

const BaseImage = styled.div<BaseImageType>`
  display: inline-block;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center center;

  html.no-js &&,
  &[data-in-view='true'] {
    ${({ sources }) => generateBackgroundsFromProps(sources)};
  }
`;

const PictureImage = styled.picture`
  display: flex;
`;

function Image(props: ImagePropTypes) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '200px',
    fallbackInView: true,
  });

  if (!props?.sources?.small['1x']) {
    return <StyledIconImage iconSize={16} />;
  }

  if (props.useIntrinsicSizing) {
    return (
      <PictureImage>
        {props.sources.large && (
          <source
            srcSet={`${encodeURI(props.sources.large['1x'])}${
              props.sources.large['2x']
                ? `, ${encodeURI(props.sources.large['2x'])} 2x`
                : ''
            }`}
            media={`(min-width: ${screenLgMin})`}
          />
        )}
        {props.sources.medium && (
          <source
            srcSet={`${encodeURI(props.sources.medium['1x'])}${
              props.sources.medium['2x']
                ? `, ${encodeURI(props.sources.medium['2x'])} 2x`
                : ''
            }`}
            media={`(min-width: ${screenSmMin})`}
          />
        )}
        <img
          className={props.className}
          src={encodeURI(props.sources.small['1x'])}
          srcSet={
            props.sources.small['2x']
              ? `${encodeURI(props.sources.small['2x'])} 2x`
              : undefined
          }
          alt={props.label ?? ''}
          loading="lazy"
        />
      </PictureImage>
    );
  }

  return <BaseImage ref={ref} data-in-view={inView} {...props} />;
}

export default Image;
